import { z } from 'zod';

export const emailConnectorProviderTypeSchema = z.enum([
  'azure',
  'resend',
  'sendgrid',
  'nodemailer',
]);

export type EmailConnectorProviderType = z.infer<typeof emailConnectorProviderTypeSchema>;

export const azureCredentialsSchema = z.object({
  tenant_id: z.string().min(1),
  client_id: z.string().min(1),
  client_secret: z.string().min(1),
  webhook_secret: z.string().optional(),
});

export const resendCredentialsSchema = z.object({
  api_key: z.string().min(1),
});

export const sendgridCredentialsSchema = z.object({
  api_key: z.string().min(1),
});

export const nodemailerCredentialsSchema = z.object({
  host: z.string().min(1),
  port: z.coerce.number().int().min(1).max(65535).default(587),
  secure: z.coerce.boolean().default(false),
  user: z.string().min(1),
  pass: z.string().min(1),
});

export const emailConnectorCredentialsSchema = z.union([
  azureCredentialsSchema,
  resendCredentialsSchema,
  sendgridCredentialsSchema,
  nodemailerCredentialsSchema,
]);

export type EmailConnectorCredentials = z.infer<typeof emailConnectorCredentialsSchema>;

export const emailConnectorSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  provider_type: emailConnectorProviderTypeSchema,
  credentials: z.record(z.string(), z.unknown()).default({}),
  email_addresses: z.array(z.string().email()).default([]),
  is_active: z.boolean().default(true),
  created_at: z.string().or(z.date()),
  updated_at: z.string().or(z.date()),
});

export type EmailConnector = z.infer<typeof emailConnectorSchema>;

export const createEmailConnectorSchema = z.object({
  name: z.string().min(1, 'Connector name is required').max(255),
  provider_type: emailConnectorProviderTypeSchema,
  credentials: z.record(z.string(), z.unknown()),
  email_addresses: z.array(z.string().email()).min(1, 'At least one email address is required'),
});

export type CreateEmailConnectorDto = z.infer<typeof createEmailConnectorSchema>;

export const updateEmailConnectorSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  credentials: z.record(z.string(), z.unknown()).optional(),
  email_addresses: z.array(z.string().email()).optional(),
  is_active: z.boolean().optional(),
});

export type UpdateEmailConnectorDto = z.infer<typeof updateEmailConnectorSchema>;
